<template>
  <div id="app" data-testid="app-root" data-qa="app-root" :data-state="appState">
    <AppUpdater />
    <component :is="layout">
      <router-view v-slot="{ Component }">
        <transition name="fade" mode="out-in">
          <component :is="Component" />
        </transition>
      </router-view>
    </component>
    <NotificationContainer />
    <Loader
      v-if="shouldShowLoader && !loaderDisabled"
      :variant="loaderVariant"
      :message="loaderMessage"
      :subtitle="loaderSubtitle"
      :size="loaderSize"
      :color="loaderColor"
      :show-spinner="loaderShowSpinner"
      :show-logo="loaderShowLogo"
      :backdrop="loaderBackdrop"
      test-id="global-loader"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useAuth } from '@/domains/auth';
import {
  isGlobalLoading,
  loaderVariant,
  loaderMessage,
  loaderSubtitle,
  loaderSize,
  loaderColor,
  loaderShowSpinner,
  loaderShowLogo,
  loaderBackdrop,
  loaderDisabled,
  getResetLoaderConfig,
  setLoaderConfig,
} from '@/core/loading/globalLoadingStore';
import { NotificationContainer } from '@/components/organisms';
import { Loader } from '@/components/molecules';
import AppUpdater from '@/components/AppUpdater.vue';

defineOptions({ name: 'App' });

const route = useRoute();
const { status: authStatus } = useAuth();

const resetLoaderConfig = getResetLoaderConfig();

// Auto-reset config when all loading stops
const shouldShowLoader = computed(() => {
  if (authStatus.isReconnecting.value) {
    setLoaderConfig({
      variant: 'card',
      message: 'Reconnecting...',
      subtitle: 'Please wait while we restore your session',
      size: 'sm',
      backdrop: true,
    });
    return true;
  }
  if (authStatus.isLoading.value) {
    return true;
  }
  return isGlobalLoading.value;
});
const appState = computed(() =>
  shouldShowLoader.value && !loaderDisabled.value ? 'loading' : 'ready'
);

watch(shouldShowLoader, (show) => {
  if (!show) {
    // Reset config (with minimum display time delay if there's a message)
    resetLoaderConfig();
  }
});

const layout = computed(() => {
  const layoutName = route.meta.layout || 'DefaultLayout';
  return defineAsyncComponent(() => import(`@/components/templates/${layoutName}.vue`));
});
</script>
